import { CreditScore, PriceCheck } from '@mui/icons-material';
import { Box, Chip, Skeleton, Tooltip } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import moment from 'moment';
import { UseFormGetValues } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import {
  CounterpartyDropDown,
  DocumentType,
  Iban,
  Invoice,
  InvoiceExpenseType,
  InvoicePatch,
  InvoiceQueryFilters,
  InvoiceSection,
  InvoiceSortColumns,
  InvoiceStages,
  PaymentChannel,
  PaymentStatus,
  SortOrder
} from 'openapi';

import {
  ACTIONS,
  APPROVERS,
  COLUMNS_DEFAULT_OPTIONS,
  COUNTERPARTY_BG_REG_NUMBER_LENGTH_REGEX,
  COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
  DATE_FORMATS,
  INITIAL_IBAN_ITEM
} from 'utils/constants/constants';
import { FILTER_BAR_HEIGHT } from 'utils/constants/filters';
import {
  VAT_NUMBER_PREFIX_BULGARIA,
  INITIAL_SORT,
  PAYMENT_CHANNELS_TRANSLATION_KEYS
} from 'utils/constants/invoices';
import { CreatedDocument, InvoiceDetails } from 'utils/interfaces/InvoiceProps';
import { TranslateFunction } from 'utils/interfaces/Translations';

import { sortDates } from './dates';

export const isVatNumberEqualCRN = (
  vatNumber: string,
  companyRegNumber?: string
) => {
  if (
    vatNumber &&
    companyRegNumber &&
    vatNumber.startsWith(VAT_NUMBER_PREFIX_BULGARIA)
  ) {
    return !(vatNumber === `BG${companyRegNumber}`);
  }

  return false;
};

export const conditions: Record<
  string,
  (value: string, values: any) => boolean
> = {
  companyRegistrationNumber: (
    value: string | undefined,
    values: any
  ): boolean =>
    (value && value.length <= 0) ||
    isVatNumberEqualCRN(values.vatNumber, value),
  dueDate: (value: string) => value.length <= 0,
  vatNumber: (value: string, values: any): boolean =>
    value.length <= 0 ||
    isVatNumberEqualCRN(value, values.companyRegistrationNumber)
};

export const decimalFormater = (value: number | null): number | string => {
  return value !== null ? Number(value).toFixed(2) : '0.00';
};

export const getInvoiceColumns = (
  isInAllCompanies: boolean,
  translate: TranslateFunction
): GridColDef[] => {
  const columns: GridColDef[] = [
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.STAGE,
      headerName: translate('labels.stage'),
      minWidth: 110,
      maxWidth: 110,
      headerAlign: 'center',
      align: 'center',
      resizable: false,
      flex: undefined
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.SHORT_COMPANY_NAME,
      headerName: translate('labels.shortCompanyName'),
      minWidth: 100,
      headerAlign: 'left',
      align: 'left'
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.COUNTERPARTY_NAME,
      headerName: translate('labels.counterparty'),
      minWidth: 275,
      headerAlign: 'left',
      align: 'left'
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.INVOICE_NUMBER,
      headerName: translate('labels.number'),
      headerAlign: 'left',
      align: 'left',
      minWidth: 120
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.INVOICE_DATE,
      headerName: translate('labels.date'),
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      sortComparator: sortDates
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.INVOICE_AMOUNT,
      headerName: translate('labels.amount'),
      minWidth: 130,
      headerAlign: 'right',
      align: 'right'
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.EXPENSE_TYPE,
      headerName: translate('labels.expenseType'),
      minWidth: 100,
      headerAlign: 'left',
      align: 'left'
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.PAYMENT_STATUS,
      headerName: translate('labels.payment'),
      minWidth: 150,
      align: 'left',
      headerAlign: 'left',
      resizable: false
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: InvoiceSortColumns.INSERTED_BY,
      headerName: translate('labels.insertedBy'),
      minWidth: 120,
      align: 'left',
      headerAlign: 'left'
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: APPROVERS,
      headerName: translate('labels.approvalProgress'),
      headerAlign: 'left',
      align: 'left',
      minWidth: 200,
      sortable: false
    },
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: ACTIONS,
      headerName: '',
      minWidth: 40,
      maxWidth: 110,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      resizable: false,
      flex: undefined
    }
  ];
  return isInAllCompanies ? columns : [columns[0], ...columns.slice(2)];
};

export const handleSortModelChange = (
  sortModel: GridSortModel,
  setFilters: (value: React.SetStateAction<InvoiceQueryFilters>) => void
) => {
  const hasSortModel = !!sortModel.length;
  const { field, sort } = hasSortModel ? sortModel[0] : { field: '', sort: '' };

  setFilters((prevFilters) =>
    hasSortModel
      ? {
          ...prevFilters,
          sortBy: field as InvoiceSortColumns,
          sortOrder: sort?.toUpperCase() as SortOrder
        }
      : {
          ...prevFilters,
          ...INITIAL_SORT
        }
  );
};

export const getCounterpartyNameRow = (
  row: any,
  column: GridColDef,
  translate: (
    module: string,
    params?: {
      [key: string]: string;
    }
  ) => string
) => {
  const fieldValue = row.isReimbursement
    ? row.reimbursementCounterpartyName || row[column.field]
    : row[column.field];

  if (row.isReimbursement) {
    const reimbursementName = row.reimbursementCounterpartyName || fieldValue;
    return reimbursementName
      ? `${translate('labels.reimburseTo')}: ${reimbursementName}`
      : '-';
  }

  return fieldValue || '-';
};

export const getCounterpartyDisplayName = (invoice: Invoice): string => {
  const {
    isReimbursement,
    counterpartyName,
    counterpartyRegistrationNumber,
    counterpartyVatNumber,
    reimbursementCounterpartyName
  } = invoice;

  if (isReimbursement) {
    return reimbursementCounterpartyName || '-';
  }

  // Both registration number and VAT number are available
  if (counterpartyRegistrationNumber && counterpartyVatNumber) {
    return `${counterpartyName} (${counterpartyRegistrationNumber}, ${counterpartyVatNumber})`;
  }
  // Only registration number is available
  if (counterpartyRegistrationNumber) {
    return `${counterpartyName} (${counterpartyRegistrationNumber})`;
  }
  // Only VAT number is available
  if (counterpartyVatNumber) {
    return `${counterpartyName} (${counterpartyVatNumber})`;
  }
  // Neither registration number nor VAT number are available
  return counterpartyName as string;
};

export const getSummaryCounterpartyDisplay = (invoice: Invoice) => {
  const isReimbursement = Boolean(invoice.isReimbursement);
  const hasFile = Boolean(invoice.filePath);
  const counterpartyValue =
    getCounterpartyDisplayName({
      ...invoice,
      isReimbursement: false
    }) || '-';

  return {
    counterpartyValue,
    showCounterparty:
      hasFile || (!isReimbursement && counterpartyValue !== '-'),
    showReimburseTo: isReimbursement
  };
};

// Reimbursements are paid to the reimbursement participant instead of the
// supplier, so their IBAN lives on its own field and is always the one selected
// for payment.
export const getInvoiceDisplayIbans = (invoice: Invoice): Iban[] => {
  if (invoice.isReimbursement) {
    return invoice.reimbursementIban
      ? [{ iban: invoice.reimbursementIban, isSelectedForPayment: true }]
      : [];
  }

  return invoice.ibans ?? [];
};

export const getInvoiceColumnsWithSkeleton = (row: any, columnName: string) => {
  const rowContent =
    columnName === InvoiceSortColumns.INVOICE_NUMBER
      ? row[columnName] || '-'
      : row[columnName];
  return row[InvoiceSortColumns.STAGE] === InvoiceStages.UPLOADED ? (
    <Box sx={{ width: '100%' }}>
      <Skeleton animation="wave" />
    </Box>
  ) : (
    rowContent
  );
};

export const getCounterPartySearchOptionLabel = (
  option: CounterpartyDropDown
) => {
  if (!option.registrationNumber && !option.vatNumber) {
    return option.name || '';
  }

  return `${option.name || ''} ${`(${option.registrationNumber || ''}${
    option.vatNumber && option.registrationNumber ? ', ' : ''
  }${option.vatNumber || ''})`}`;
};

export const getSupplierDefaultValues = (currentInvoice: Invoice) => ({
  counterpartyName: currentInvoice.counterpartyName || '',
  counterpartyRegistrationNumber:
    currentInvoice.counterpartyRegistrationNumber || '',
  counterpartyVatNumber: currentInvoice.counterpartyVatNumber || '',
  ibans: currentInvoice.ibans?.length
    ? currentInvoice.ibans?.map((iban) => ({ ...iban, tmpId: uuidv4() }))
    : [
        {
          ...INITIAL_IBAN_ITEM,
          ...COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
          tmpId: uuidv4()
        }
      ],
  isReimbursement: currentInvoice.isReimbursement || false
});

export const getInvoiceDataDefaultValues = (currentInvoice: Invoice) => ({
  invoiceNumber: currentInvoice.invoiceNumber || '',
  invoiceDate: currentInvoice.invoiceDate,
  dueDate: currentInvoice.dueDate,
  vatBase: currentInvoice.vatBase?.toFixed(2) || '',
  vatAmount: currentInvoice.vatAmount?.toFixed(2) || '',
  invoiceAmount: currentInvoice.invoiceAmount?.toFixed(2) || '',
  expenseType: currentInvoice.expenseType?.id,
  currencyId: currentInvoice.currencyId || '',
  documentDescription: currentInvoice.documentDescription || '',
  tags: currentInvoice.tags || [],
  paymentChannel: currentInvoice.paymentChannel || PaymentChannel.COMPANY_CARD,
  documentType: currentInvoice.documentType,
  paymentStatus: currentInvoice.paymentStatus,
  counterpartyId: currentInvoice.counterpartyId
});

export const getCreatedDocumentDefaultValues = (currentInvoice: Invoice) => {
  const isReimbursement = currentInvoice.isReimbursement || false;

  const reimbursementIbans = currentInvoice.reimbursementIban
    ? [
        {
          ...INITIAL_IBAN_ITEM,
          ...COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
          iban: currentInvoice.reimbursementIban,
          tmpId: uuidv4()
        }
      ]
    : null;

  const counterpartyIbans = currentInvoice.ibans?.length
    ? currentInvoice.ibans?.map((iban) => ({ ...iban, tmpId: uuidv4() }))
    : [
        {
          ...INITIAL_IBAN_ITEM,
          ...COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
          tmpId: uuidv4()
        }
      ];

  return {
    counterpartyName: isReimbursement
      ? currentInvoice.reimbursementCounterpartyName || ''
      : currentInvoice.counterpartyName || '',
    counterpartyRegistrationNumber:
      currentInvoice.counterpartyRegistrationNumber || '',
    counterpartyVatNumber: currentInvoice.counterpartyVatNumber || '',
    ibans:
      isReimbursement && reimbursementIbans
        ? reimbursementIbans
        : counterpartyIbans,
    invoiceDate:
      currentInvoice.invoiceDate ||
      moment().format(DATE_FORMATS.displayedDateFormat),
    invoiceAmount: currentInvoice.invoiceAmount?.toFixed(2) || '',
    expenseType: currentInvoice.expenseType?.id || '',
    currencyId: currentInvoice.currencyId || '',
    isPrepaid: currentInvoice.paymentStatus === PaymentStatus.PREPAID,
    isReimbursement,
    documentDescription: currentInvoice.documentDescription || '',
    tags: currentInvoice.tags || [],
    paymentChannel: currentInvoice.paymentChannel || PaymentChannel.COMPANY_CARD
  };
};

export const getSendForApprovalDefaultValues = (currentInvoice: Invoice) => ({
  companyId: currentInvoice.companyId,
  documentType: currentInvoice.documentType || DocumentType.INVOICE,
  isReimbursement: currentInvoice.isReimbursement || false,
  isPrepaid: currentInvoice.paymentStatus === PaymentStatus.PREPAID,
  paymentChannel:
    currentInvoice.paymentStatus === PaymentStatus.PREPAID
      ? currentInvoice.paymentChannel || PaymentChannel.BANK
      : undefined,
  counterpartyId: currentInvoice.counterpartyId,
  counterpartyName: currentInvoice.counterpartyName || '',
  counterpartyRegistrationNumber:
    currentInvoice.counterpartyRegistrationNumber || '',
  counterpartyVatNumber: currentInvoice.counterpartyVatNumber || '',
  isCounterpartyValidated: currentInvoice.isCounterpartyValidated || false,
  ibans: currentInvoice.ibans?.length
    ? currentInvoice.ibans?.map((iban) => ({ ...iban, tmpId: uuidv4() }))
    : [
        {
          ...INITIAL_IBAN_ITEM,
          ...COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
          tmpId: uuidv4()
        }
      ],
  reimbursementCounterpartyId: currentInvoice.isReimbursement
    ? currentInvoice.reimbursementCounterpartyId
    : undefined,
  reimbursementCounterpartyName: currentInvoice.isReimbursement
    ? currentInvoice.reimbursementCounterpartyName || ''
    : undefined,
  reimbursementIban: currentInvoice.isReimbursement
    ? currentInvoice.reimbursementIban || ''
    : '',
  invoiceNumber: currentInvoice.invoiceNumber || '',
  invoiceDate: currentInvoice.invoiceDate || '',
  dueDate: currentInvoice.dueDate || '',
  vatBase: currentInvoice.vatBase?.toFixed(2) || '',
  vatAmount: currentInvoice.vatAmount?.toFixed(2) || '',
  invoiceAmount: currentInvoice.invoiceAmount?.toFixed(2) || '',
  expenseType: currentInvoice.expenseType?.id ?? '',
  currencyId: currentInvoice.currencyId ?? undefined,
  documentDescription: currentInvoice.documentDescription || '',
  tags: currentInvoice.tags || []
});

export const validateIban = (iban: string): boolean => {
  try {
    iban = iban.replace(/\s+/g, '').toUpperCase();
    const rearrangedIban = iban.slice(4) + iban.slice(0, 4);
    const numericIban = rearrangedIban
      .split('')
      .map((char) => {
        const code = char.charCodeAt(0);
        return code >= 65 && code <= 90 ? (code - 55).toString() : char;
      })
      .join('');
    const mod97 = BigInt(numericIban) % BigInt(97);
    return mod97 === BigInt(1);
  } catch {
    return false;
  }
};

export const isCounterpartyVatNumberValid = (
  vatNumber: string,
  companyRegNumber?: string
) => {
  const slicedVatNumber = vatNumber.slice(2, vatNumber.length);
  if (
    vatNumber &&
    companyRegNumber &&
    vatNumber.startsWith(VAT_NUMBER_PREFIX_BULGARIA)
  ) {
    return vatNumber === `BG${companyRegNumber}`;
  }

  if (companyRegNumber && slicedVatNumber !== companyRegNumber) {
    return false;
  }

  return true;
};

export const getContainerHeight = (
  areFiltersVisible: boolean,
  expandedHeight: number,
  isWithoutQuickFilter?: boolean
) => {
  const barHeight = isWithoutQuickFilter ? FILTER_BAR_HEIGHT : -5;
  const expHeight = isWithoutQuickFilter ? expandedHeight : 0;
  const filtersHeight = areFiltersVisible ? expHeight : barHeight;

  return `calc(100% - ${filtersHeight}px)`;
};

export const validateRegistrationNumber = (
  watchedCounterpartyVatNumber: string | undefined,
  watchedCounterpartyRegistrationNumber: string | undefined
) => {
  const vatNumber = watchedCounterpartyVatNumber || '';
  const registrationNumber = watchedCounterpartyRegistrationNumber || '';

  if (!registrationNumber) {
    return true;
  }

  const isBulgarianFormat =
    COUNTERPARTY_BG_REG_NUMBER_LENGTH_REGEX.test(registrationNumber);

  if (vatNumber) {
    return vatNumber.startsWith('BG') ? isBulgarianFormat : false;
  }

  return isBulgarianFormat;
};

export const validateVatNumber = (
  watchedCounterpartyVatNumber: string | undefined,
  watchedCounterpartyRegistrationNumber: string | undefined
) => {
  const vatNumber = watchedCounterpartyVatNumber || '';
  const registrationNumber = watchedCounterpartyRegistrationNumber || '';

  if (!vatNumber) {
    return true;
  }

  if (vatNumber.length <= 2 || !/^[A-Za-z]{2}/.test(vatNumber)) {
    return false;
  }

  if (vatNumber.startsWith(VAT_NUMBER_PREFIX_BULGARIA)) {
    return vatNumber.substring(2) === registrationNumber;
  }

  return true;
};

export const getCreatedDocumentFormValues = (
  selectedCounterPartyId: number | null,
  currentInvoice: Invoice,
  isExisting: boolean,
  getValues: UseFormGetValues<CreatedDocument>,
  formErrors?: any
) => {
  const isReimbursement = getValues('isReimbursement');
  const counterpartyId = isExisting
    ? selectedCounterPartyId ||
      (isReimbursement
        ? currentInvoice.reimbursementCounterpartyId
        : currentInvoice.counterpartyId)
    : null;

  const ibans = getValues('ibans')?.map((iban) => iban.iban);

  const baseValues = {
    counterpartyId,
    counterpartyName: getValues('counterpartyName') || null,
    counterpartyRegistrationNumber:
      getValues('counterpartyRegistrationNumber') || null,
    counterpartyVatNumber: getValues('counterpartyVatNumber') || null,
    invoiceAmount: Number(getValues('invoiceAmount')),
    expenseTypeId: Number(getValues('expenseType')) || null,
    currencyId: getValues('currencyId') || null,
    isPrepaid: isReimbursement ? false : getValues('isPrepaid'),
    isReimbursement,
    documentDescription: getValues('documentDescription') || null,
    tags: getValues('tags'),
    paymentChannel: getValues('isPrepaid')
      ? getValues('paymentChannel')
      : undefined,
    ibans,
    reimbursementIban: isReimbursement ? ibans?.[0] || null : undefined
  };

  return {
    ...baseValues,
    invoiceDate: formErrors?.invoiceDate
      ? currentInvoice.invoiceDate
      : getValues('invoiceDate')
  } as InvoicePatch;
};

export const getInvoiceDataFormValues = (
  currentInvoice: Invoice,
  expenseTypeOptions: InvoiceExpenseType[],
  getValues: UseFormGetValues<InvoiceDetails>
) => {
  return {
    invoiceNumber: getValues('invoiceNumber'),
    invoiceDate: getValues('invoiceDate'),
    dueDate: getValues('dueDate'),
    vatBase: Number(getValues('vatBase')),
    vatAmount: Number(getValues('vatAmount')),
    invoiceAmount: Number(getValues('invoiceAmount')),
    expenseTypeId: expenseTypeOptions?.find(
      (expenseType) => expenseType.id === Number(getValues('expenseType'))
    )?.id,
    currencyId: Number(getValues('currencyId')),
    documentDescription: getValues('documentDescription'),
    tags: getValues('tags')
  };
};

export const getSummaryDataFormValues = (
  currentInvoice: Invoice,
  expenseTypeOptions: InvoiceExpenseType[],
  getValues: UseFormGetValues<InvoiceDetails>
) => ({
  ...getInvoiceDataFormValues(currentInvoice, expenseTypeOptions, getValues),
  invoiceNumber: getValues('invoiceNumber') || undefined,
  paymentChannel: getValues('paymentChannel'),
  documentType: getValues('documentType') as DocumentType,
  paymentStatus: getValues('paymentStatus') as PaymentStatus,
  counterpartyId: getValues('counterpartyId') || undefined
});

export const getSaveInvoiceDataFormValues = (
  currentInvoice: Invoice,
  getValues: UseFormGetValues<InvoiceDetails>,
  formErrors?: any
) => {
  const baseValues = {
    invoiceNumber: getValues('invoiceNumber'),
    vatBase: Number(getValues('vatBase')),
    vatAmount: Number(getValues('vatAmount')),
    invoiceAmount: Number(getValues('invoiceAmount')),
    expenseTypeId: getValues('expenseType'),
    currencyId: Number(getValues('currencyId')) || 1,
    documentDescription: getValues('documentDescription'),
    tags: getValues('tags')
  };

  return {
    ...baseValues,
    invoiceDate: formErrors?.invoiceDate
      ? currentInvoice.invoiceDate
      : getValues('invoiceDate'),
    dueDate: formErrors?.dueDate ? currentInvoice.dueDate : getValues('dueDate')
  } as InvoicePatch;
};

export const getSupplierDataFormValues = (
  currentInvoice: Invoice,
  isExisting: boolean,
  selectedCounterPartyId: number | null,
  getValues: UseFormGetValues<Invoice>
) => {
  const counterpartyId = isExisting
    ? selectedCounterPartyId || currentInvoice.counterpartyId
    : null;

  return {
    counterpartyId,
    counterpartyName: getValues('counterpartyName'),
    counterpartyRegistrationNumber: getValues('counterpartyRegistrationNumber'),
    counterpartyVatNumber: getValues('counterpartyVatNumber'),
    isReimbursement: getValues('isReimbursement')
  } as InvoicePatch;
};

export const getPaymentIcon = (
  row: any,
  translate: (
    module: string,
    params?: {
      [key: string]: string;
    }
  ) => string
) => {
  if (row.isPrepaid) {
    let label = translate('labels.prePaid');
    if (row.paymentChannel) {
      label = `${label} / ${translate(
        PAYMENT_CHANNELS_TRANSLATION_KEYS[row.paymentChannel as PaymentChannel]
      )}`;
    }

    return (
      <Tooltip title={label}>
        <PriceCheck
          sx={{
            fontSize: '1.1rem'
          }}
        />
      </Tooltip>
    );
  }

  if (!row.isPayable) {
    let label = translate('labels.paid');
    if (row.paymentChannel) {
      label = `${label} / ${translate(
        PAYMENT_CHANNELS_TRANSLATION_KEYS[row.paymentChannel as PaymentChannel]
      )}`;
    }

    return (
      <Tooltip title={label}>
        <CreditScore
          sx={{
            fontSize: '1.1rem'
          }}
        />
      </Tooltip>
    );
  }

  return null;
};

export const getPaymentStatusChip = (
  currentInvoice: Invoice,
  translate: (
    module: string,
    params?: {
      [key: string]: string;
    }
  ) => string
) => {
  if (currentInvoice.paymentStatus === PaymentStatus.PREPAID) {
    return (
      <Chip
        color="success"
        label={translate('labels.prePaid')}
        sx={{
          height: 18,
          fontSize: '0.75rem'
        }}
      />
    );
  }

  if (currentInvoice.paymentStatus !== PaymentStatus.NOT_PAID) {
    return (
      <Chip
        color="success"
        label={translate('labels.paid')}
        sx={{
          height: 18,
          fontSize: '0.75rem'
        }}
      />
    );
  }

  return null;
};

export const getInvoiceStepperActiveStep = (currentInvoice: Invoice) => {
  const orderedInvoiceSections = Object.keys(InvoiceSection) as Array<
    keyof typeof InvoiceSection
  >;
  let verifiedSteps = 0;

  orderedInvoiceSections.forEach((section) => {
    switch (section) {
      case InvoiceSection.COMPANY:
        if (!currentInvoice.isCompanyVerified) {
          return;
        }
        verifiedSteps += 1;
        break;
      case InvoiceSection.COUNTERPARTY:
        if (!currentInvoice.isCounterpartyVerified) {
          return;
        }
        verifiedSteps += 1;
        break;
      case InvoiceSection.DOCUMENT_DATA:
        if (!currentInvoice.isDocumentDataVerified) {
          return;
        }
        if (
          currentInvoice.stage === InvoiceStages.APPROVED ||
          currentInvoice.stage === InvoiceStages.SETTLED
        ) {
          verifiedSteps += 2;
          return;
        }
        if (currentInvoice.stage === InvoiceStages.FINALIZED) {
          verifiedSteps += 3;
          return;
        }
        if (currentInvoice.stage === InvoiceStages.REJECTED) {
          verifiedSteps += 4;
          return;
        }
        verifiedSteps += 1;
        break;
      default:
        break;
    }
  });

  return verifiedSteps;
};
